import { UnifiedSearchResultModel, Client as SearchClient } from '@bizzkit/ecommerce-search/client';
import {
	type InfiniteData,
	QueryClient,
	type QueryKey,
	useInfiniteQuery,
	type UseInfiniteQueryResult,
	useQuery,
	UseQueryResult,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { queryKeys } from '@/api/apiConfig';
import { mapProductItem } from '@/api/product/mapping';
import { ProductItemResponse } from '@/api/product/types';
import queryStaleTimes from '@/api/queryStaleTimes';
import { listCustomer } from '@/generated/customer';
import { type ListCustomerParams, ProblemDetails, SearchSettingsResponse } from '@/generated/data.schemas';

export interface FetchProductBandParams {
	readonly searchApiHost: string;
	readonly searchToken: string;
	readonly searchSegment: string;
	readonly bandsScopeId: string;
	readonly productIds: string[];
}

export const fetchProductBand = async (params: FetchProductBandParams): Promise<UnifiedSearchResultModel> => {
	const searchClient = new SearchClient({ baseUrl: params.searchApiHost });
	const result = await searchClient.search.search({
		segmentId: params.searchSegment,
		scopeId: params.bandsScopeId,
		authenticationToken: params.searchToken,
		filters: { SearchableIds: { values: params.productIds } },
	});
	return result.data;
};

export const customerSegmentServerSideFetch = (
	queryClient: QueryClient,
	ids: string[],
	settings: SearchSettingsResponse,
): Promise<void> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings.searchApiHost,
		bandsScopeId: settings.bandsScopeId,
		searchSegment: settings.searchSegment,
		searchToken: settings.searchToken,
		productIds: ids,
	};

	return queryClient.prefetchQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
	});
};

export const useProductBandQuery = (
	ids: string[],
	isActive: boolean,
	settings?: SearchSettingsResponse,
): UseQueryResult<ProductItemResponse[] | null | undefined, undefined | ProblemDetails> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		bandsScopeId: settings?.bandsScopeId ?? '',
		searchSegment: settings?.searchSegment ?? '',
		searchToken: settings?.searchToken ?? '',
		productIds: ids,
	};

	return useQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
		enabled: !!settings && ids.length > 0 && isActive,
		select: (d) => {
			return d.products?.map((p) => mapProductItem(p, settings)) || null;
		},
	});
};

export const customerPageSize = 10;

export const useListCustomerInfinite = <
	TData = InfiniteData<Awaited<ReturnType<typeof listCustomer>>>,
	TError = AxiosError<unknown>,
>(
	segmentationId: number,
	selectedCustomerIds?: number[],
	searchTerm?: string,
	enabled?: boolean,
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {
	const params: ListCustomerParams = {
		ExcludeCustomerIds: selectedCustomerIds,
		PageSize: customerPageSize,
		SearchTerm: searchTerm,
	};

	const queryOptions = {
		queryKey: queryKeys.customers.list(segmentationId, params).queryKey,
		queryFn: ({ pageParam = 1 }) => {
			// Pass the current pageParam to the backend as `PageNumber`
			return listCustomer(
				segmentationId,
				{ ...params, PageNumber: pageParam as number },
				{
					paramsSerializer: {
						indexes: null, // needed to serialize array-type params correctly
					},
				},
			);
		},
		enabled: enabled,
		initialPageParam: 1,
		getNextPageParam: (_lastPage, allPages) => {
			const totalPages = Math.ceil((_lastPage.data.totalCount || 1) / customerPageSize);
			const nextPage = allPages.length + 1;
			return nextPage <= totalPages ? nextPage : undefined;
		},
		staleTime: queryStaleTimes.customer,
	};

	return useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };
};
